import { AlertTriangle, Clock, LogOut } from 'lucide-react'
import { ABSOLUTE_TIMEOUT_MS, IDLE_TIMEOUT_MS } from '../lib/sessionSecurity'

function formatRemainingTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

function formatDurationFromMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  if (totalSeconds % 3600 === 0 && totalSeconds >= 3600) {
    const hours = totalSeconds / 3600
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  }
  if (totalSeconds % 60 === 0 && totalSeconds >= 60) {
    const minutes = totalSeconds / 60
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
  }
  if (totalSeconds >= 3600) {
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.round((totalSeconds % 3600) / 60)
    if (minutes === 0) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
  }
  if (totalSeconds >= 60) {
    return `${Math.round(totalSeconds / 60)} minutes`
  }
  return `${totalSeconds} ${totalSeconds === 1 ? 'second' : 'seconds'}`
}

export default function SessionTimeoutWarning({
  secondsRemaining,
  onContinue,
  onSignOut,
}: {
  secondsRemaining: number
  onContinue: () => void
  onSignOut: () => void
}) {
  const idleDescription = formatDurationFromMs(IDLE_TIMEOUT_MS)
  const absoluteDescription = formatDurationFromMs(ABSOLUTE_TIMEOUT_MS)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/55 px-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-description"
        className="w-full max-w-md rounded-xl border border-warning/20 bg-white p-6 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning/10 text-warning">
            <AlertTriangle size={20} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 id="session-timeout-title" className="text-lg font-bold text-neutral-900">
              Session Expiring Soon
            </h2>
            <p id="session-timeout-description" className="mt-2 text-sm leading-relaxed text-neutral-600">
              For security, CETA signs users out after {idleDescription} of inactivity and after {absoluteDescription} total session time.
              Continue your session to remain signed in.
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-lg border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-amber-800">
          <div className="flex items-center gap-2 font-semibold">
            <Clock size={16} aria-hidden="true" />
            Automatic sign-out in {formatRemainingTime(secondsRemaining)}
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onSignOut}
            className="inline-flex items-center justify-center gap-2 rounded border border-neutral-300 px-4 py-2.5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <LogOut size={16} aria-hidden="true" />
            Sign Out
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="inline-flex items-center justify-center rounded bg-primary px-4 py-2.5 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-primary-dark"
          >
            Continue Session
          </button>
        </div>
      </div>
    </div>
  )
}
